<template>
  <DeepDialog
    v-if="currentContextOrgId"
    :closable="false"
    max-width="700"
    :model-value="show"
    :title="t('overlays.overused_subscription.title')"
  >
    <template #content>
      <v-card-item class="pt-0 pb-4 px-1">
        <MenuOrganizationSelection v-if="currentContextOrgId" max-width="400" />
      </v-card-item>
      <v-alert
        class="ma-0"
        density="compact"
        icon="far fa-exclamation-triangle"
        type="warning"
        variant="tonal"
      >
        <template #title>
          <span class="text-body-large">
            {{ getTitle }}
          </span>
        </template>
        <template #text>
          <span class="text-body-medium">
            {{ getText }}
          </span>
        </template>
      </v-alert>
      <v-card-text class="text-body-small px-0 pb-0">
        {{ t('overlays.overused_subscription.learn_more') }}

        <DeepLink :href="linkSubscriptions">support.deepcloud.swiss</DeepLink>
      </v-card-text>
    </template>

    <template #actions>
      <VBtnTertiary v-if="!isProdEnv()" color="error" @click="ignore">
        {{ t('buttons.ignore_dev_int_only') }}
      </VBtnTertiary>
      <template
        v-if="
          deepAdminUsersCompanyAccountingStore.companyAccounting
            .has_overused_box_users
        "
      >
        <template v-if="isOwner">
          <v-spacer />
          <VBtnSecondary :href="getDeepAdminOrgInfoUrl(currentContextOrgId)">
            {{
              t(
                'overlays.overused_subscription.has_overused_box_users.owner.actions.manage_organization',
              )
            }}
          </VBtnSecondary>
          <VBtnPrimary
            :href="getDeepAdminOrgSubscriptionUrl(currentContextOrgId)"
          >
            {{
              t(
                'overlays.overused_subscription.has_overused_box_users.owner.actions.manage_subscription',
              )
            }}
          </VBtnPrimary>
        </template>
      </template>
      <template
        v-else-if="
          deepAdminUsersCompanyAccountingStore.companyAccounting
            .has_a_blocking_amount_of_open_invoices
        "
      >
        <v-spacer />
        <VBtnPrimary
          v-if="isOwner"
          :href="getDeepAdminOrgSubscriptionInvoiceUrl(currentContextOrgId)"
        >
          {{
            t(
              'overlays.overused_subscription.has_a_blocking_amount_of_open_invoices.owner.actions.open_invoices',
            )
          }}
        </VBtnPrimary>
        <VBtnPrimary v-else :href="deepcloudDashboardURL">
          {{
            t(
              'overlays.overused_subscription.has_a_blocking_amount_of_open_invoices.admin_or_member.actions.support',
            )
          }}
        </VBtnPrimary>
      </template>
    </template>
  </DeepDialog>
</template>
<script lang="ts" setup>
import { isProdEnv } from '@/utils/helpers/env.ts'
import { DeepDialog, DeepLink } from '@deepcloud/deep-ui-lib'
import { useDeepAdminUsersCompanyAccountingStore } from '@/stores/deepadmin/users/users-company-accounting'
import { useAppsUrls } from '@/composables/apps-urls'
import { useKeycloak } from '@/composables/auth/keycloak'
import { useKnowledgeCenter } from '@/composables/knowledge-center'
import { useOrganization } from '@/composables/organization/organization'
import { useOrganizationRoles } from '@/composables/organization/organization-roles'
import MenuOrganizationSelection from '@/components/menu/MenuOrganizationSelection.vue'

const { t } = useI18n()
const { user } = useKeycloak()

const deepAdminUsersCompanyAccountingStore =
  useDeepAdminUsersCompanyAccountingStore()

const { currentContextOrgId } = useOrganization()

const deepcloudDashboardURL = import.meta.env
  .VITE_DEEPCLOUD_DASHBOARD_FRONTEND_BASE_URL

const ignored = useLocalStorage(
  `ignore_overused_deepbox_overlay_${user.value?.sub}`,
  {},
)

const isOrgIgnored = computed(() => {
  if (!currentContextOrgId.value) return false
  return Object.keys(ignored.value).includes(currentContextOrgId.value)
})

const isAccountingOverused = computed(
  () =>
    deepAdminUsersCompanyAccountingStore.companyAccounting
      .has_overused_box_users ||
    deepAdminUsersCompanyAccountingStore.companyAccounting
      .has_a_blocking_amount_of_open_invoices,
)

const show = computed(() => {
  if (isProdEnv()) {
    return isAccountingOverused.value
  }
  return isAccountingOverused.value && !isOrgIgnored.value
})

const { isOwner } = useOrganizationRoles()

const getTitle = computed(() => {
  if (
    deepAdminUsersCompanyAccountingStore.companyAccounting
      .has_overused_box_users
  ) {
    return t('overlays.overused_subscription.has_overused_box_users.title')
  } else if (
    deepAdminUsersCompanyAccountingStore.companyAccounting
      .has_a_blocking_amount_of_open_invoices
  ) {
    return t(
      'overlays.overused_subscription.has_a_blocking_amount_of_open_invoices.title',
    )
  }
  return undefined
})

const getText = computed(() => {
  if (
    deepAdminUsersCompanyAccountingStore.companyAccounting
      .has_overused_box_users
  ) {
    return isOwner.value
      ? t('overlays.overused_subscription.has_overused_box_users.owner.text')
      : t(
          'overlays.overused_subscription.has_overused_box_users.admin_or_member.text',
        )
  } else if (
    deepAdminUsersCompanyAccountingStore.companyAccounting
      .has_a_blocking_amount_of_open_invoices
  ) {
    return isOwner.value
      ? t(
          'overlays.overused_subscription.has_a_blocking_amount_of_open_invoices.owner.text',
        )
      : t(
          'overlays.overused_subscription.has_a_blocking_amount_of_open_invoices.admin_or_member.text',
        )
  }
  return undefined
})

const { linkSubscriptions } = useKnowledgeCenter()

const ignore = () => {
  ignored.value = {
    ...ignored.value,
    [`${currentContextOrgId.value}`]: true,
  }
}

const {
  getDeepAdminOrgInfoUrl,
  getDeepAdminOrgSubscriptionUrl,
  getDeepAdminOrgSubscriptionInvoiceUrl,
} = useAppsUrls()
</script>
